import Splide from '@splidejs/splide';
import { gsap } from 'gsap';
import { Translations } from '../utils';
import './timeline-slider.scss';

export class TimelineSlider {
  constructor() {
    this.el = document.querySelector('.js-timeline-slider');
    if (!this.el) return;

    this.splideEl = this.el.querySelector('.timeline-slider__splide');
    this.years = this.el.querySelectorAll('.timeline-slider__year');
    this.prevBtn = this.el.querySelector('.timeline-slider__arrow--prev');
    this.nextBtn = this.el.querySelector('.timeline-slider__arrow--next');
    this.progress = this.el.querySelector('.timeline-slider__progress');
    this.line = this.el.querySelector('.timeline-slider__line');
    this.yearsWrapper = this.el.querySelector('.timeline-slider__years');

    this.init();
  }

  init() {
    this.splide = new Splide(this.splideEl, {
      type: 'fade',
      rewind: false,
      arrows: false,
      accessibility: false,
      pagination: false,
      drag: false,
      speed: 400,
      autoHeight: true,
      i18n: Translations.getSplideI18n(),
    });

    this.splide.mount();

    const removeAriaHidden = () => {
      this.splideEl.querySelectorAll('.splide__slide').forEach((slide) => {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('tabindex');
      });
    };

    // intercetta TUTTI gli eventi che reimpostano aria-hidden
    this.splide.on('mounted move updated refresh resized active inactive', removeAriaHidden);

    // rimuovi aria-hidden anche subito dopo il mount
    removeAriaHidden();

    this.bindEvents();
    this.updateNav(this.splide.index);
    this.initMobileGalleries();

    window.addEventListener('resize', () => {
      this.animateProgress(this.splide.index);
      this.updateMobileTimeline(this.splide.index);
    });
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        if (this.splide.index > 0) {
          this.splide.go('<');
        }
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        if (this.splide.index < this.splide.length - 1) {
          this.splide.go('>');
        }
      });
    }

    this.years.forEach((year, index) => {
      year.addEventListener('click', () => {
        this.splide.go(index);
      });
    });

    this.splide.on('move', (newIndex) => {
      this.updateNav(newIndex);
    });
  }

  updateNav(index) {
    if (index < 0 || index >= this.years.length) return;

    this.years.forEach((year) => {
      year.classList.remove('is-active');
      year.classList.add('is-clickable');
    });

    this.years[index].classList.add('is-active');

    if (index > 0) {
      this.years[index - 1].classList.add('is-clickable');
      this.prevBtn.disabled = false;
    } else {
      this.prevBtn.disabled = true;
    }

    if (index < this.years.length - 1) {
      this.years[index + 1].classList.add('is-clickable');
      this.nextBtn.disabled = false;
    } else {
      this.nextBtn.disabled = true;
    }

    this.animateProgress(index);
    this.updateMobileTimeline(index);
  }

  animateProgress(index) {
    const activeYear = this.years[index];
    if (!activeYear) return;

    const dot = activeYear.querySelector('.timeline-slider__dot');
    if (!dot) return;

    const lineRect = this.line.getBoundingClientRect();
    const dotRect = dot.getBoundingClientRect();

    let offset = dotRect.top - lineRect.top + dotRect.height / 2;

    const isLastSlide = (index === this.years.length - 1) && (index !== 0);

    if (isLastSlide) {
      offset += 35;
    }

    gsap.to(this.progress, {
      height: offset,
      duration: 0.4,
      ease: 'power2.out',
    });
  }

  updateMobileTimeline(index) {
    if (!this.yearsWrapper) return;

    if (window.innerWidth > 768) return;

    const firstYear = this.years[0];
    if (!firstYear) return;

    const step = 146;
    const translate = index * step;

    gsap.to(this.yearsWrapper, {
      x: -translate,
      duration: 0.4,
      ease: 'power2.out',
    });
  }

  initMobileGalleries() {
    if (window.innerWidth > 768) return;

    const galleries = this.el.querySelectorAll('.timeline-slider__gallery');

    galleries.forEach((gallery) => {
      const images = gallery.querySelectorAll('.timeline-slider__image');

      if (images.length <= 1) return;

      gallery.classList.add('splide', 'timeline-slider__gallery-splide');

      const track = document.createElement('div');
      track.classList.add('splide__track');

      const list = document.createElement('ul');
      list.classList.add('splide__list');

      images.forEach((image) => {
        const slide = document.createElement('li');
        slide.classList.add('splide__slide');

        slide.appendChild(image);
        list.appendChild(slide);
      });

      track.appendChild(list);
      gallery.appendChild(track);

      const arrows = document.createElement('div');
      arrows.classList.add('timeline-slider__gallery-arrows');

      arrows.innerHTML = `
      <button class="timeline-slider__gallery-arrow timeline-slider__gallery-arrow--prev" title="${Translations.getLabel('TIMELINE_SLIDER_GALLERY_ARROW_PREV_TITLE', {}, 'prev slide')}">
        <svg role="img" class="svg-icon svg-icon--prev-thin" aria-label="${Translations.getLabel('TIMELINE_SLIDER_GALLERY_ARROW_PREV_ARIA_LABEL', {}, 'prev-icon')}">
          <use xlink:href="#sprite-prev-thin"></use>
        </svg>
      </button>
      <button class="timeline-slider__gallery-arrow timeline-slider__gallery-arrow--next" title="${Translations.getLabel('TIMELINE_SLIDER_GALLERY_ARROW_NEXT_TITLE', {}, 'next slide')}">
        <svg role="img" class="svg-icon svg-icon--next-thin" aria-label="${Translations.getLabel('TIMELINE_SLIDER_GALLERY_ARROW_NEXT_ARIA_LABEL', {}, 'next-icon')}">
          <use xlink:href="#sprite-next-thin"></use>
        </svg>
      </button>
    `;

      gallery.appendChild(arrows);

      const prev = arrows.querySelector('.timeline-slider__gallery-arrow--prev');
      const next = arrows.querySelector('.timeline-slider__gallery-arrow--next');

      const splide = new Splide(gallery, {
        arrows: false,
        pagination: false,
        drag: true,
        accessibility: false,
        gap: 16,
        perPage: 1,
        padding: { right: '20%' },
        i18n: Translations.getSplideI18n(),
      });

      splide.mount();

      const removeAriaHidden = () => {
        this.splideEl.querySelectorAll('.splide__slide').forEach((slide) => {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('tabindex');
        });
      };

      // intercetta TUTTI gli eventi che reimpostano aria-hidden
      splide.on('mounted move updated refresh resized active inactive', removeAriaHidden);

      // rimuovi aria-hidden anche subito dopo il mount
      removeAriaHidden();

      splide.on('mounted move', () => {
        document.querySelectorAll('.splide__slide').forEach((slide) => {
          slide.removeAttribute('aria-hidden');
        });
      });

      const updateArrows = () => {
        const { index } = splide;
        const last = splide.length - 1;

        prev.classList.remove('is-disabled');
        next.classList.remove('is-disabled');

        if (index === 0) {
          prev.classList.add('is-disabled');
        }

        if (index === last) {
          next.classList.add('is-disabled');
        }
      };

      updateArrows();

      splide.on('move', () => {
        updateArrows();
      });

      prev.addEventListener('click', () => {
        if (!prev.classList.contains('is-disabled')) {
          splide.go('<');
        }
      });

      next.addEventListener('click', () => {
        if (!next.classList.contains('is-disabled')) {
          splide.go('>');
        }
      });
    });
  }
}
